import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CompanyService {

  constructor(private http: HttpClient) { }

  getCompany(id): Observable<any> {
    return this.http.get(`${environment.url}/company/${id}`);
  }

  saveCompany(id, company): Observable<any> {
    return this.http.put(`${environment.url}/company/${id}`, company);
  }
  /** 
   * @deprecated
  */
  getUsersOfCompany(): Observable<any> {
    return this.http.get(`${environment.url}/company/all/users`);
  }

  getTypeOfBusiness(): Observable<any> {
    return this.http.get(`${environment.url}/company/type/business`)
  }

  uploadLogo(photo: any): Observable<any> {
    return this.http.post(`${environment.url}/company/upload-logo`, photo)
  }

  getAllUsers({ dato = '', sortField = '', sortDir = '', status = 'active' }): Observable<any> {
    let params = new HttpParams()
      .set('dato', dato)
      .set('sortField', sortField)
      .set('sortDir', sortDir)
      .set('status', status);
    return this.http.get(`${environment.url}/lbn/user/all/company`, {
      params
    });
  }

  getCompanies(query: { limit?: number, page?: number, dato?: string }): Observable<any> {
    let params = new HttpParams().set('limit', query.limit || 10).set('page', query.page || 1).set('dato', query.dato || '');

    return this.http.get(`${environment.url}/lbn/company/find-all/sa`, {
      params
    }).pipe(
      tap(console.log)
    )
  }

  updateCompanyStatus({ companyId, status }) {
    return this.http.put(`${environment.url}/company/update-status/${companyId}/sa`, {
      status
    })
  }
  updateNightlyPurge({ companyId, nightlyPurge }) {
    return this.http.put(`${environment.url}/company/update-enightly-purge/${companyId}/sa`, {
      nightlyPurge
    })
  }

  refreshCache(): Observable<{ success: boolean; keysCleared: number }> {
    return this.http.post<{ success: boolean; keysCleared: number }>(
      `${environment.url}/company/refresh-cache/sa`,
      {},
    );
  }

  getQeueNumber() {
    return this.http.get(`${environment.url}/company/get-queue-number/sa`)
  }

  createCompany(company) {
    return this.http.post(`${environment.url}/auth/create-account-sa/sa`, {
      ...company
    })
  }

  editCompany({ _id, ...restCompany }): Observable<any> {
    return this.http.put(`${environment.url}/lbn/company/${_id}/sa`, {
      ...restCompany
    })
  }

  integrateTMS(id: string, serviceKey: string) {
    return this.http.put(`${environment.url}/company/update-tms/${id}/sa`, { serviceKey: serviceKey })
  }

  integrateFTP(id: string, { name }) {
    return this.http.put(`${environment.url}/company/update-ftp/${id}/sa`, { integrationFormat: name })
  }

  /** Zoho hosted page where the company admin replaces the subscription card. */
  startCardUpdate(): Observable<{ status: boolean; data: { url: string } }> {
    return this.http.post<{ status: boolean; data: { url: string } }>(`${environment.url}/zoho/billing/update-card`, {});
  }

  /**
   * Settings → Billing: save billingInformation and push to Zoho customer when linked.
   * Does not update company profile / load boards.
   */
  saveBillingContact(billingInformation: Record<string, unknown>): Observable<{
    status: boolean;
    data: {
      billingInformation: Record<string, unknown>;
      zohoSynced: boolean;
      zohoSkippedReason?: string;
    };
  }> {
    return this.http.put<{
      status: boolean;
      data: {
        billingInformation: Record<string, unknown>;
        zohoSynced: boolean;
        zohoSkippedReason?: string;
      };
    }>(`${environment.url}/zoho/billing/contact`, billingInformation);
  }

  /** Cached Zoho contact persons (extra billing emails). refresh=true pulls from Zoho. */
  listBillingContactPersons(refresh = false): Observable<{
    status: boolean;
    data: {
      contactPersons: Array<{ contactpersonId: string; email: string; firstName?: string; lastName?: string }>;
      refreshedFromZoho: boolean;
    };
  }> {
    const q = refresh ? '?refresh=1' : '';
    return this.http.get<{
      status: boolean;
      data: {
        contactPersons: Array<{ contactpersonId: string; email: string; firstName?: string; lastName?: string }>;
        refreshedFromZoho: boolean;
      };
    }>(`${environment.url}/zoho/billing/contact-persons${q}`);
  }

  addBillingContactPerson(body: {
    email: string;
    firstName?: string;
    lastName?: string;
  }): Observable<{
    status: boolean;
    data: {
      contactPersons: Array<{ contactpersonId: string; email: string; firstName?: string; lastName?: string }>;
      associated?: boolean;
      portalInvited?: boolean;
    };
  }> {
    return this.http.post<{
      status: boolean;
      data: {
        contactPersons: Array<{ contactpersonId: string; email: string; firstName?: string; lastName?: string }>;
        associated?: boolean;
        portalInvited?: boolean;
      };
    }>(`${environment.url}/zoho/billing/contact-persons`, body);
  }

  removeBillingContactPerson(contactpersonId: string): Observable<{
    status: boolean;
    data: {
      contactPersons: Array<{ contactpersonId: string; email: string; firstName?: string; lastName?: string }>;
    };
  }> {
    return this.http.delete<{
      status: boolean;
      data: {
        contactPersons: Array<{ contactpersonId: string; email: string; firstName?: string; lastName?: string }>;
      };
    }>(`${environment.url}/zoho/billing/contact-persons/${encodeURIComponent(contactpersonId)}`);
  }

  updateBillingFlags(companyId: string, flags: { suspendExempt?: boolean; needsBillingReview?: boolean }): Observable<any> {
    return this.http.put(`${environment.url}/zoho/billing/companies/${companyId}/flags/sa`, flags);
  }

  /** Billing tab rows. No query string: GlobalStore routes responses by URL words. */
  getBillingCompanies(): Observable<{ status: boolean; data: any[] }> {
    return this.http.get<{ status: boolean; data: any[] }>(`${environment.url}/zoho/billing/companies/sa`);
  }

  sendPortalInvite(companyId: string): Observable<{ status: boolean; data: { outcome: string; message: string } }> {
    return this.http.post<{ status: boolean; data: { outcome: string; message: string } }>(
      `${environment.url}/zoho/billing/companies/${companyId}/portal-invite/sa`,
      {},
    );
  }

  /** SA: demo → paid checkout URL + email to company. Does not open Zoho in the SA browser. */
  startBilling(companyId: string, planCode: string): Observable<{
    status: boolean;
    data: { url: string; emailSent: boolean; emailedTo: string | null };
  }> {
    return this.http.post<{
      status: boolean;
      data: { url: string; emailSent: boolean; emailedTo: string | null };
    }>(`${environment.url}/zoho/billing/companies/${companyId}/start-billing/sa`, { planCode });
  }

  /** Demo company admin: same Zoho customer checkout; caller opens the returned URL. */
  startDemoSubscription(planCode: string): Observable<{ status: boolean; data: { url: string } }> {
    return this.http.post<{ status: boolean; data: { url: string } }>(
      `${environment.url}/zoho/billing/start-subscription`,
      { planCode },
    );
  }

  /** M8: compare Zoho-linked companies with Zoho. dryRun = preview only (nothing changes). */
  runBillingCheck(dryRun: boolean): Observable<{ status: boolean; data: any }> {
    return this.http.post<{ status: boolean; data: any }>(`${environment.url}/zoho/billing/reconcile/sa`, { dryRun });
  }

  intercept(id: any, data: any) {
    if (data.type === 'ftp') {
      return this.integrateFTP(id, data)
    } else {
      return this.integrateTMS(id, data.serviceKey)
    }
  }
}